import React, { useState, useEffect, useRef } from "react";

import { X } from "lucide-react";

import { getInitials } from "../../Utils/Helper/Helper";
import InitialAvatar from "../Avatar";
import Button from "../Button";
import CommonInput from "../Input";
import "./style.scss";

export interface Option {
  label: string;
  value: string;
}

interface MultiSelectProps {
  options: Option[];
  selected: Option[];
  onChange: (selected: Option[]) => void;
  placeholder?: string;
  showAvatar?: boolean;
  isLoading?: boolean;
  label?: string;
  required?: boolean;
  error?: string;
  className?: string;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  selected,
  onChange,
  placeholder = "Select...",
  showAvatar = true,
  isLoading = false,
  label,
  required,
  error,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => setIsOpen(prev => !prev);

  const handleSelect = (option: Option) => {
    if (!selected.find(s => s.value === option.value)) {
      onChange([...selected, option]);
    }
    setInputValue(""); // reset input
    setIsOpen(true); // keep dropdown open
  };

  const handleRemove = (option: Option) => {
    onChange(selected.filter(s => s.value !== option.value));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (!isOpen) setIsOpen(true);
  };

  const filteredOptions = options.filter(
    opt =>
      !selected.find(s => s.value === opt.value) &&
      opt.label.toLowerCase().includes(inputValue.toLowerCase())
  );

  // Optional: close dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className={`w-full ${className}`.trim()} ref={containerRef}>
      <div className="relative">
        <div className="cursor-text" onClick={toggleDropdown}>
          <CommonInput
            label={label}
            value={inputValue}
            onChange={e => {
              handleInputChange(e);
              setIsOpen(true);
            }}
            placeholder={placeholder}
            type="text"
            required={required}
            name="input-value"
            error={error}
            className="!mb-0"
          />
        </div>
        {isOpen && (
          <div className="absolute left-0 top-full z-50 mt-1 max-h-60 min-w-full overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
            {isLoading ? (
              <div className="px-4 py-2 text-sm text-gray-500">Loading...</div>
            ) : filteredOptions.length === 0 ? (
              <div className="px-4 py-2 text-sm text-gray-500">No results</div>
            ) : (
              filteredOptions.map(option => (
                <div
                  key={option.value}
                  className="cursor-pointer px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-100"
                  onClick={() => handleSelect(option)}
                >
                  {option.label}
                </div>
              ))
            )}
          </div>
        )}
      </div>
      {selected?.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2 pb-1">
          {selected.map(option => (
            <div
              className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50 px-2 py-1"
              key={option.value}
            >
              {showAvatar && (
                <InitialAvatar initials={getInitials(option.label)} />
              )}
              <span className="text-sm font-medium text-gray-700">
                {option.label}
              </span>
              <Button
                variant="clear"
                className="inline-flex h-5 w-5 items-center justify-center rounded-full p-0 text-gray-500 transition-colors hover:bg-emerald-100 hover:text-gray-700"
                onClick={() => handleRemove(option)}
              >
                <X
                  style={{
                    height: 13,
                    width: 13,
                  }}
                />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
